import { CenteredContentLayout } from 'components/containers/CenteredContentLayout';
import React, { useContext } from 'react';
import { CategoryCard } from './CategoryCard';
import { CreateEditCategoryModal } from './CreateEditCategoryModal';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import {
  GetCategories_workspace_categories,
  GetQueueMetadata_workspace_queueMeta
} from '../../../models/legacy-graphql';
import { ButtonGroup } from '../../../components/containers/ButtonGroup';
import classes from './Categories.module.scss';
import classNames from 'classnames';
import { GetTimeslotsQuery } from '../../../models/graphql';
import { EmptyPlaceholder } from '../../../components/containers/EmptyPlaceholder';
import emptyArchivedCategory from 'assets/images/emptyArchivedCategory.svg';
import { usePauseSettings } from 'hooks/usePauseSettings';
import { BeaconContext } from '../../../providers/BeaconProvider';

export type CategoriesViewProps = {
  loading: boolean;
  isArchivedView: boolean;
  openCreateEditCategoryModal: boolean;
  setOpenCreateEditCategoryModal: (openInviteModal: boolean) => void;
  categories: GetCategories_workspace_categories[];
  queueMetadata: GetQueueMetadata_workspace_queueMeta[];
  timeslots: GetTimeslotsQuery['workspace']['timeslots'];
  counts: Record<string | number, number | undefined>;
};

export const ThirdNavLeftButtons = (
  isArchivedView: boolean,
  categoryFilterHandler: (url: string) => void
): JSX.Element => {
  return (
    <ButtonGroup size={'medium'}>
      <Button
        type={'button'}
        variant={!isArchivedView ? 'solid' : 'hollow'}
        size={'medium'}
        color={ButtonColorsEnum.primary}
        action={() => categoryFilterHandler('/publish/categories')}>
        Active
      </Button>
      <Button
        type={'button'}
        variant={isArchivedView ? 'solid' : 'hollow'}
        size={'medium'}
        color={ButtonColorsEnum.primary}
        action={() => categoryFilterHandler('/publish/categories/archived')}>
        Archived
      </Button>
    </ButtonGroup>
  );
};

export const ThirdNavRightButtons = (onAddCategory: VoidFunction): JSX.Element => {
  return (
    <>
      <div className={'hide-for-small-only'}>
        <Button
          type={'button'}
          variant={'solid'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          icon={{ side: 'left', value: IconsEnum.plus }}
          action={onAddCategory}>
          Add Category
        </Button>
      </div>
      <div className={'show-for-small-only'}>
        <Button
          type={'button'}
          variant={'solid'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          icon={{ side: 'center', value: IconsEnum.plus }}
          action={onAddCategory}
        />
      </div>
    </>
  );
};

export const CategoriesView = (props: CategoriesViewProps): JSX.Element => {
  const isLastCategory = props.categories.length < 2;
  const { queueMetadata, counts } = props;
  const { isCategoryPausedForAllSocialProfiles } = usePauseSettings();
  const { beacon } = useContext(BeaconContext);

  const pausedCategories = queueMetadata ? queueMetadata.filter(category => category.paused) : [];

  const categoryCards: JSX.Element[] = props.categories.map((category, index) => {
    const isPaused = !!pausedCategories.find(pausedCategory => pausedCategory?.category?.id === category.id) ?? false;
    const timeslotCount = props.timeslots?.filter(timeslot => timeslot.category.id === category.id).length;
    const isAllSocialProfilesPaused = isCategoryPausedForAllSocialProfiles(category.id);
    return (
      <div key={category.id} className={'margin-bottom-small-8 cell small-12 tablet-portrait-6 relative'}>
        <CategoryCard
          isFirst={index === 0}
          category={category}
          isLastCategory={isLastCategory}
          isPaused={isPaused}
          timeslotCount={timeslotCount}
          postsCount={counts[category.id] ?? 0}
          isAllSocialProfilesPaused={isAllSocialProfilesPaused}
        />
      </div>
    );
  });

  const noArchivedMessageClasses = classNames({
    cell: true,
    'small-12': true,
    'margin-top-small-24': true,
    [classes.NoArchivedMessage]: true
  });

  return (
    <CenteredContentLayout loading={props.loading}>
      <div className={'grid-x grid-padding-x margin-bottom-small-32'}>
        {categoryCards.length === 0 && props.isArchivedView ? (
          <div className={noArchivedMessageClasses}>
            <EmptyPlaceholder title={'You have no Archived Categories'} image={emptyArchivedCategory}>
              <div>
                Archived Categories let you temporarily remove content from your posting schedule without deleting it.
                They don’t count toward your plan’s limits and can be reactivated anytime.{' '}
                <a onClick={() => beacon('article', '691', { type: 'modal' })}>
                  Learn more
                </a>
              </div>
            </EmptyPlaceholder>
          </div>
        ) : (
          <>{categoryCards}</>
        )}
      </div>
      {props.openCreateEditCategoryModal && (
        <CreateEditCategoryModal onClose={() => props.setOpenCreateEditCategoryModal(false)} />
      )}
    </CenteredContentLayout>
  );
};
